/**
 * OperationsSection - WBC3 Operations Configuration Component
 * Handles special operations, international voyages, and risk assessment levels
 * Based on RA-verified WBC3 requirements for proper equipment/manning calculations
 */

import React, { useEffect, useCallback, useMemo } from 'react';
import { Globe, Anchor, Activity, AlertTriangle, FileCheck, Ship } from 'lucide-react';
import WizardSection from '../../WizardSection';
import { vesselTypeOperations, vesselTypePresets } from '../../../tabs/utils/vesselTypePresets';

// WBC3-verified special operations with regulatory references
const SPECIAL_OPERATIONS = [
  { 
    value: 'dangerous_goods', 
    label: 'Dangerous Goods Operations', 
    icon: AlertTriangle,
    help: 'Transport of dangerous goods - triggers additional fire pumps',
    reference: 'WBC3 Section 29'
  },
  { 
    value: 'towing_operations', 
    label: 'Towing Operations', 
    icon: Anchor,
    help: 'Commercial towing services - requires winch specifications',
    reference: 'WBC3 Section 26'
  },
  { 
    value: 'pilot_transfer', 
    label: 'Pilot Transfer Operations', 
    icon: Ship,
    help: 'Pilot boarding/landing - requires special LSA equipment',
    reference: 'WBC3 Section 27'
  },
  { 
    value: 'personnel_transfer', 
    label: 'Personnel Transfer', 
    icon: Activity,
    help: 'Crew transfer operations - enhanced safety requirements',
    reference: 'WBC3 general provisions'
  },
  { 
    value: 'cargo_operations', 
    label: 'Cargo Operations', 
    icon: Activity,
    help: 'Cargo handling and transport - stability requirements',
    reference: 'WBC3 loading/stability'
  },
  { 
    value: 'mgo_transfer_ops', 
    label: 'MGO Transfer Operations', 
    icon: Activity,
    help: 'Marine Gas Oil transfer operations - requires endorsement',
    reference: 'WBC3 Section 29.10'
  },
  { 
    value: 'high_speed_ops', 
    label: 'High-Speed Operations', 
    icon: Activity,
    help: 'Operations ≥20 knots or planing mode - requires endorsement',
    reference: 'WBC3 Section 25.4'
  },
  { 
    value: 'survey_operations', 
    label: 'Survey Operations', 
    icon: Activity,
    help: 'Hydrographic/seismic surveys - positioning equipment',
    reference: 'WBC3 navigation requirements'
  },
  { 
    value: 'dive_support', 
    label: 'Dive Support', 
    icon: Activity,
    help: 'Commercial diving operations - enhanced safety protocols',
    reference: 'WBC3 + diving regulations'
  },
  { 
    value: 'lifting', 
    label: 'Lifting Operations', 
    icon: Activity,
    help: 'Crane and lifting equipment - LOLER compliance',
    reference: 'WBC3 + LOLER requirements'
  },
  { 
    value: 'patrol_operations', 
    label: 'Patrol Operations', 
    icon: Activity,
    help: 'Security/enforcement patrols - may require police boat cert',
    reference: 'WBC3 Annex 3 (if police)'
  },
  { 
    value: 'passenger_operations', 
    label: 'Passenger Operations', 
    icon: Activity,
    help: 'Commercial passenger service - enhanced LSA requirements',
    reference: 'WBC3 passenger provisions'
  }
];

// Risk assessment levels
const RISK_ASSESSMENT_LEVELS = {
  'basic': {
    label: 'Basic Risk Assessments',
    help: 'Covers mandatory risk assessments for operations and equipment'
  },
  'comprehensive': {
    label: 'Comprehensive Risk Assessments', 
    help: 'Includes general operations RAs (mooring, anchoring, deck work)'
  }
};

const OperationsSection = ({ draftConfig, onUpdate }) => {
  // Auto-select operations based on vessel type (certificate-based)
  useEffect(() => {
    const vesselType = draftConfig.vesselType;
    const endorsements = draftConfig.selectedEndorsements || [];
    
    if (vesselType) {
      let requiredOps = [];
      
      // First try to get operations from preset (for exact matches)
      const preset = vesselTypePresets[vesselType];
      if (preset?.operations) {
        requiredOps = preset.operations;
      } else {
        // For dynamic certificate types (multiple endorsements), derive operations from endorsements
        requiredOps = [];
        
        // Map endorsements to operations
        if (endorsements.includes('towing')) requiredOps.push('towing_operations');
        if (endorsements.includes('pilot_boat')) requiredOps.push('pilot_transfer');
        if (endorsements.includes('mgo_transfer')) requiredOps.push('mgo_transfer_ops');
        if (endorsements.includes('high_speed')) requiredOps.push('high_speed_ops');
        
        // If no endorsements but certificate type implies operations, use fallback
        if (requiredOps.length === 0) {
          requiredOps = vesselTypeOperations[vesselType] || [];
        }
      }
      
      if (requiredOps.length > 0) {
        const currentOps = draftConfig.operations || [];
        
        // Get all auto-selectable operations from presets AND vesselTypeOperations (to know what to remove)
        const allPresetOps = Object.values(vesselTypePresets).map(p => p.operations || []).flat();
        const allTypeOps = Object.values(vesselTypeOperations).flat();
        const endorsementOps = ['towing_operations', 'pilot_transfer', 'mgo_transfer_ops', 'high_speed_ops'];
        const allAutoOps = [...allPresetOps, ...allTypeOps, ...endorsementOps];
        
        // Remove any previously auto-selected operations that don't belong to current vessel type
        const userSelectedOps = currentOps.filter(op => !allAutoOps.includes(op));
        
        // Combine user selections with required operations for current vessel type
        const newOps = [...userSelectedOps, ...requiredOps];
        
        // Only update if the operations actually changed
        if (JSON.stringify(newOps.sort()) !== JSON.stringify(currentOps.sort())) {
          onUpdate({ operations: newOps });
        }
      } else {
        // If vessel type has no auto-operations, remove any auto-selected ones
        const currentOps = draftConfig.operations || [];
        const allPresetOps = Object.values(vesselTypePresets).map(p => p.operations || []).flat();
        const allTypeOps = Object.values(vesselTypeOperations).flat();
        const endorsementOps = ['towing_operations', 'pilot_transfer', 'mgo_transfer_ops', 'high_speed_ops'];
        const allAutoOps = [...allPresetOps, ...allTypeOps, ...endorsementOps];
        const userSelectedOps = currentOps.filter(op => !allAutoOps.includes(op));
      
        if (userSelectedOps.length !== currentOps.length) {
          onUpdate({ operations: userSelectedOps });
        }
      }
    }
  }, [draftConfig.vesselType, draftConfig.selectedEndorsements, onUpdate]);

  const handleOperationToggle = useCallback((operation) => {
    const endorsements = draftConfig.selectedEndorsements || [];
    const baseCertificate = draftConfig.baseCertificate || draftConfig.vesselType;
    
    // Validate non-self-propelled platforms cannot have commercial operations
    if (baseCertificate === 'non_self_propelled') {
      alert('🏗️ Platform Operations Restriction\n\nNon-self-propelled platforms are unmanned working platforms per WBC3 Section 26.5.1.1.\n\nCommercial operations are not applicable to unmanned platforms. Only platform-specific safety requirements apply under selective assessment framework.');
      return; // Prevent selection
    }
    
    // Validate towing operations require proper endorsement
    if (operation === 'towing_operations') {
      if (!endorsements.includes('towing')) {
        alert('⚠️ Towing Operations Require Endorsement\n\nPer WBC3 Section 26.1.5: "A vessel engaged in towing shall be issued with a Workboat Certificate with a towing endorsement."\n\nPlease add the Towing Endorsement in Step 1 to enable towing operations.');
        return; // Prevent selection
      }
    }
    
    // Validate MGO transfer operations require proper endorsement
    if (operation === 'mgo_transfer_ops') {
      if (!endorsements.includes('mgo_transfer')) {
        alert('⚠️ MGO Transfer Operations Require Endorsement\n\nPer WBC3 Section 29.10.2.1.2: "Certificate shall be endorsed to permit this operation."\n\nPlease add the MGO Transfer Endorsement in Step 1 to enable MGO transfer operations.');
        return; // Prevent selection
      }
    }
    
    // Validate high-speed operations require proper endorsement
    if (operation === 'high_speed_ops') {
      if (!endorsements.includes('high_speed')) {
        alert('⚠️ High-Speed Operations Require Endorsement\n\nPer WBC3 Section 25.4: "High-speed operations require appropriate endorsement."\n\nPlease add the High-Speed Endorsement in Step 1 to enable high-speed operations.');
        return; // Prevent selection
      }
    }
    
    // Validate pilot transfer operations require proper endorsement
    if (operation === 'pilot_transfer') {
      if (!endorsements.includes('pilot_boat') && draftConfig.vesselType !== 'pilot_boat_dedicated') {
        alert('⚠️ Pilot Transfer Operations Require Endorsement\n\nPer WBC3 Section 27: "Pilot transfer operations require appropriate certification."\n\nPlease add the Pilot Boat Endorsement in Step 1 or select Dedicated Pilot Boat Certificate to enable pilot transfer operations.');
        return; // Prevent selection
      }
    }
    
    const current = draftConfig.operations || [];
    const updated = current.includes(operation)
      ? current.filter(o => o !== operation)
      : [...current, operation];
    onUpdate({ operations: updated });
  }, [draftConfig.selectedEndorsements, draftConfig.baseCertificate, draftConfig.vesselType, draftConfig.operations, onUpdate]);

  const handleFieldChange = useCallback((field, value) => {
    onUpdate({ [field]: value });
  }, [onUpdate]);

  const isInland = draftConfig.operatingStatus === 'inland';
  
  return (
    <div className="space-y-6">
      {/* International Voyages - Seagoing Only */}
      {!isInland && (
        <WizardSection.Card>
          <WizardSection.Header>International Operations</WizardSection.Header>
          
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-blue-600" />
              <div>
                <div className="font-medium text-gray-900">International Voyages</div>
                <div className="text-sm text-gray-600">
                  Voyages beyond UK territorial waters - enhanced GMDSS required
                </div>
              </div>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={draftConfig.internationalVoyages || false}
                onChange={(e) => handleFieldChange('internationalVoyages', e.target.checked)}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
            </label>
          </div>
          
          {draftConfig.internationalVoyages && (
            <WizardSection.Callout variant="info" title="International Voyage Requirements">
              Enhanced GMDSS equipment required per SOLAS. Navigation and communication 
              systems will be upgraded automatically based on intended operating areas.
            </WizardSection.Callout>
          )}
        </WizardSection.Card>
      )}

      {/* Special Operations */}
      <WizardSection.Card>
        <WizardSection.Header>Special Operations</WizardSection.Header>
        <WizardSection.Text>
          Select additional operations your vessel will perform. These affect equipment requirements, 
          manning standards, and certification needs.
        </WizardSection.Text>

        {/* Platform-Specific Notice */}
        {(draftConfig.baseCertificate || draftConfig.vesselType) === 'non_self_propelled' && (
          <WizardSection.Callout variant="regulatory" title="Platform Operations Notice">
            <strong>WBC3 Section 26.5.1.1:</strong> Non-self-propelled platforms operate under selective assessment framework.
            Commercial operations are not applicable to unmanned working platforms. Platform-specific safety 
            requirements (emergency towing equipment, marking, stability) are covered separately.
          </WizardSection.Callout>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {SPECIAL_OPERATIONS.map((operation) => {
            const Icon = operation.icon;
            const isSelected = (draftConfig.operations || []).includes(operation.value);
            
            // Check if operations should be disabled based on endorsements and vessel type
            const endorsements = draftConfig.selectedEndorsements || [];
            const baseCertificate = draftConfig.baseCertificate || draftConfig.vesselType;
            
            // Non-self-propelled platforms cannot have ANY commercial operations (unmanned platform)
            const isPlatformDisabled = baseCertificate === 'non_self_propelled';
            
            // Endorsement-based restrictions (for manned vessels)
            const isTowingDisabled = operation.value === 'towing_operations' && !endorsements.includes('towing');
            const isMgoDisabled = operation.value === 'mgo_transfer_ops' && !endorsements.includes('mgo_transfer');
            const isHighSpeedDisabled = operation.value === 'high_speed_ops' && !endorsements.includes('high_speed');
            const isPilotDisabled = operation.value === 'pilot_transfer' && !endorsements.includes('pilot_boat') && draftConfig.vesselType !== 'pilot_boat_dedicated';
            
            const isOperationDisabled = isPlatformDisabled || isTowingDisabled || isMgoDisabled || isHighSpeedDisabled || isPilotDisabled;
            
            return (
              <div 
                key={operation.value} 
                className={`relative p-4 border rounded-lg transition-colors ${
                  isOperationDisabled
                    ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-60'
                    : isSelected 
                      ? 'bg-cyan-50 border-cyan-200 cursor-pointer' 
                      : 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'
                }`}
                onClick={() => !isOperationDisabled && handleOperationToggle(operation.value)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isOperationDisabled}
                      onChange={() => {}} // Handled by div onClick
                      className={`h-4 w-4 border-gray-300 rounded ${
                        isOperationDisabled 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : 'text-cyan-600 focus:ring-cyan-500'
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${isOperationDisabled ? 'text-gray-400' : 'text-gray-600'}`} />
                      <label className={`text-sm font-medium cursor-pointer ${
                        isOperationDisabled ? 'text-gray-500' : 'text-gray-900'
                      }`}>
                        {operation.label}
                      </label>
                    </div>
                    <p className={`text-sm mt-1 ${isOperationDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                      {operation.help}
                    </p>
                    {isOperationDisabled ? (
                      <p className="text-xs text-amber-600 mt-1">
                        {isPlatformDisabled 
                          ? '🏗️ Not applicable to unmanned platforms (WBC3 26.5.1.1)'
                          : '⚠️ Requires appropriate endorsement from Step 1'
                        }
                      </p>
                    ) : (
                      <p className="text-xs text-cyan-600 mt-1">
                        {operation.reference}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Operations Impact Warning */}
        {(draftConfig.operations || []).length > 0 && (
          <WizardSection.Callout variant="warning" title="Operations Impact">
            Selected operations will trigger additional equipment requirements, training needs, 
            and certification considerations. These will be calculated automatically in subsequent steps.
          </WizardSection.Callout>
        )}
      </WizardSection.Card>

      {/* Risk Assessment Level */}
      <WizardSection.Card>
        <WizardSection.Header>Risk Assessment Level</WizardSection.Header>
        <WizardSection.Text>
          Choose the comprehensiveness of risk assessments for your SMS. This affects 
          the number and detail of risk assessments generated.
        </WizardSection.Text>

        <div className="space-y-3 mt-4">
          {Object.entries(RISK_ASSESSMENT_LEVELS).map(([value, config]) => (
            <div key={value} className="flex items-center space-x-3">
              <input
                type="radio"
                id={`sms-${value}`}
                name="smsLevel"
                value={value}
                checked={(draftConfig.smsLevel || 'basic') === value}
                onChange={(e) => handleFieldChange('smsLevel', e.target.value)}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
              />
              <label htmlFor={`sms-${value}`} className="flex-1 cursor-pointer">
                <div className="font-medium text-gray-900">{config.label}</div>
                <div className="text-sm text-gray-600">{config.help}</div>
              </label>
            </div>
          ))}
        </div>

        {/* General Operations RA Toggle */}
        {draftConfig.smsLevel !== 'comprehensive' && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900">Include General Operations Risk Assessments</div>
                <div className="text-sm text-gray-600">
                  Add risk assessments for routine operations (mooring, anchoring, deck work) - SMS best practice
                </div>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={draftConfig.includeGeneralOperationsRA || false}
                  onChange={(e) => handleFieldChange('includeGeneralOperationsRA', e.target.checked)}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                />
              </label>
            </div>
          </div>
        )}
      </WizardSection.Card>
    </div>
  );
};

export default OperationsSection;